
import React, { useState, useEffect } from 'react';
import { AppStep, Language, CreationRecord, PointTransaction, User } from './types';
import IdeationStep from './components/IdeationStep';
import AuthModal from './components/AuthModal';
import ConfirmModal from './components/ConfirmModal';
import { dbService } from './services/dbService';
import { Zap, Globe, CheckCircle2, ChevronDown, Plus, FolderOpen, Clock, Coins, X, History, ArrowUpRight, Tag, LogOut, User as UserIcon, Trash2 } from 'lucide-react';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentStep, setCurrentStep] = useState<AppStep>(AppStep.IDEATION);
  const [lang, setLang] = useState<Language>('en');
  const [historyRecords, setHistoryRecords] = useState<CreationRecord[]>([]);
  const [pointHistory, setPointHistory] = useState<PointTransaction[]>([]);
   const [pointHistoryPage, setPointHistoryPage] = useState(1);
  const [hasMorePoints, setHasMorePoints] = useState(true);
  const [isLoadingPoints, setIsLoadingPoints] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [projectId, setProjectId] = useState<string>(() => Math.random().toString(36).substring(7));
  
  const [points, setPoints] = useState<number>(0);

  const [initialIdea, setInitialIdea] = useState('');
  const [prdData, setPrdData] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState(false);
  const [showArchiveToast, setShowArchiveToast] = useState(false);
  const [authSuccessToast, setAuthSuccessToast] = useState<{show: boolean, type: 'login' | 'register'}>({show: false, type: 'login'});
  const [confirmDelete, setConfirmDelete] = useState<{show: boolean, recordId: string | null}>({show: false, recordId: null});
  const [errorToast, setErrorToast] = useState<{show: boolean, message: string} | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      // First check for cookie-based session
      const validatedUser = await dbService.validateSession();
      if (validatedUser) {
        setUser(validatedUser);
        setPoints(validatedUser.remaining_ai_token);
      } else {
        const currentUser = dbService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          getUserPoints();
        }
      }
    };

    initAuth();

    const handleUnauthorized = () => {
      handleLogout(true);
    };

    // 获取路由参数 用户来源
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    if (source) {
      localStorage.setItem('source', source);
    }

    window.addEventListener('unauthorized_access', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized_access', handleUnauthorized);
    };
  }, []);

  useEffect(() => {
    if (user) {
      loadHistory();
      loadPointHistory();
      getUserPoints();
    }
  }, [user]);

  const handleLogout = (showModal: boolean = false) => {
    dbService.logout();
    setUser(null);
    setHistoryRecords([]);
    setPointHistory([]);
    setPoints(0);
    createNewProject();
    if (showModal) {
      setShowAuthModal(true);
    }
  };

  const handleAuthRequired = () => {
    setShowAuthModal(true);
  };


  const handleDeleteProject = async (e: React.MouseEvent, recordId: string) => {
    e.stopPropagation(); // Prevent selecting the project when clicking delete
    setConfirmDelete({ show: true, recordId });
  };

  const executeDelete = async () => {
    const recordId = confirmDelete.recordId;
    if (!recordId) return;

    try {
      await dbService.deleteProject(recordId);
      // Update local state
      setHistoryRecords(prev => prev.filter(r => r.id !== recordId));
      
      // If the deleted project was the current one, reset
      if (projectId === recordId) {
        createNewProject();
      }
      setConfirmDelete({ show: false, recordId: null });
    } catch (e) {
      console.error("Failed to delete project", e);
      setErrorToast({
        show: true,
        message: lang === 'zh' ? '删除失败' : 'Delete failed'
      });
      setTimeout(() => setErrorToast(null), 3000);
      setConfirmDelete({ show: false, recordId: null });
    }
  };

  useEffect(() => {
    if (isFinished) {
      setShowArchiveToast(true);
      const timer = setTimeout(() => {
        setShowArchiveToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isFinished, projectId]);

  useEffect(() => {
    if (showPointsModal) {
      loadPointHistory(1, false);
    }
  }, [showPointsModal]);

  const loadHistory = async () => {
    if (!user) return;
    try {
      const response = await dbService.getRemoteProjects();
      const records: CreationRecord[] = response.data.map((item: any) => ({
        id: item.id.toString(),
        projectName: item.project_name,
        initialIdea: item.project_name || item.description || "Concept",
        timestamp: new Date(item.create_time).getTime(),
        status: item.status === 1 ? 'prd_designing' : 
                item.status === 2 ? 'visual_designing' : 
                item.status === 3 ? 'eng_evaluating' : undefined,
        mainImage: dbService.normalizeImageUrl(item.images_data[0]), // Remote list might not have images
        prd: ""
      }));
      setHistoryRecords(records);
    } catch (e) {
      console.error("Failed to load history", e);
    }
  };

  const getUserPoints = async () => {
    if (!user) return;
    try {
      const { remaining: points} = await dbService.getUserPoints();
      setPoints(points);
    } catch (e) {
      console.error("Failed to get user points", e);
    }
  };

  const loadPointHistory = async (page: number = 1, append: boolean = false) => {
    if (!user) return;
    setIsLoadingPoints(true);
    try {
      const response = await dbService.getPointUsageHistory(page, 10);
      const txs: PointTransaction[] = response.data
        .filter((item: any) => item.log_type === 1) // Only show "Generate Images", hide "Engineering Evaluation" (type 2)
        .map((item: any) => ({
          id: item.id.toString(),
          amount: item.tokens,
          description: { zh: "生成图片", en: "Generate Images" },
          projectName: item.project_name,
          timestamp: new Date(item.create_time).getTime()
        }));

      if (append) {
        setPointHistory(prev => [...prev, ...txs]);
      } else {
        setPointHistory(txs);
      }
      
      setPointHistoryPage(page);
      setHasMorePoints(page < response.last_page);
    } catch (e) {
      console.error("Failed to load point history", e);
    } finally {
      setIsLoadingPoints(false);
    }
  };

  const loadMorePoints = () => {
    if (!isLoadingPoints && hasMorePoints) {
      loadPointHistory(pointHistoryPage + 1, true);
    }
  };

  const handleSelectRecord = async (record: CreationRecord) => {
    let dataToUse: any = record;

    // If it's a remote project (numeric ID), fetch full details from remote API
    if (!isNaN(Number(record.id))) {
      try {
        const remoteData = await dbService.getRemoteProjectDetails(Number(record.id));
        if (remoteData) {
          // Map remote data to local structure based on the provided API response
          const prdData: any = {
            projectName: remoteData.project_name || "",
            coreDescription: remoteData.description || "",
            scenariosPainPoints: remoteData.use_scenario_pain || "",
            userPersona: remoteData.user_portrait || "",
            appearanceMaterials: remoteData.appearance_material || "",
            featureDetails: remoteData.core_function || "",
            interactionOperation: remoteData.user_interaction || "",
            keyComponents: remoteData.key_component || "",
            controlLogic: remoteData.control_logic || "",
            performanceGoals: remoteData.performance_indicator || "",
            costBom: remoteData.cost_budget || "",
            mfgQuality: remoteData.quality_durability || "",
            sizePkg: remoteData.size_packaging || "",
            // Add other fields if they exist in the response
            supplyChain: remoteData.supply_chain || "",
            compliance: remoteData.compliance || "",
            deliveryMilestones: remoteData.delivery_milestones || ""
          };

          dataToUse = {
            id: remoteData.id.toString(),
            projectNo: remoteData.project_no,
            projectName: remoteData.project_name,
            initialIdea: remoteData.chat_record?.[0]?.content || remoteData.description || record.initialIdea,
            mainImage: remoteData?.images_data[0] || '',
            images: remoteData?.images_data || [],
            prd: JSON.stringify(prdData),
            messages: remoteData.chat_record || [],
            totalUsd: Number(remoteData.total_amount || 0),
            ver_images: remoteData?.ver_images || [],
            nowVersion: Number(remoteData.now_version || 0),
            status: remoteData.status === 1 ? 'prd_designing' : 
                remoteData.status === 2 ? 'visual_designing' : 
                remoteData.status === 3 ? 'eng_evaluating' : undefined,
          };
          // Also save/update to local DB for offline access and consistency
          // await dbService.saveRecord(dataToUse);
          
          // Update historyRecords state so IdeationStep gets the full data
          setHistoryRecords(prev => prev.map(r => r.id === dataToUse.id ? dataToUse : r));
        }
      } catch (e) {
        console.error("Failed to fetch remote project details, falling back to local/list data", e);
        const localRecord = await dbService.getRecord(record.id);
        if (localRecord) dataToUse = localRecord;
      }
    } else {
      // Try to get full record from local DB if it exists
      const fullRecord = await dbService.getRecord(record.id);
      if (fullRecord) dataToUse = fullRecord;
    }

    setProjectId(dataToUse.id); 
    setInitialIdea(dataToUse.initialIdea);
    setPrdData(dataToUse.prd || null);
    setSelectedImage(dataToUse.mainImage || null);
    setIsFinished(true); 
    setCurrentStep(AppStep.IDEATION);
  };

  const createNewProject = () => {
    setProjectId(Math.random().toString(36).substring(7));
    setInitialIdea('');
    setPrdData(null);
    setSelectedImage(null);
    setIsFinished(false);
    setShowArchiveToast(false);
    setCurrentStep(AppStep.IDEATION);
  };

  const handlePrdComplete = (prd: string, idea: string, mainImage: string) => {
    setPrdData(prd);
    setInitialIdea(idea);
    setSelectedImage(mainImage);
    
    // Update local historyRecords state instead of reloading everything from server
    if (projectId) {
      setHistoryRecords(prev => prev.map(r => {
        if (r.id === projectId) {
          return { ...r, prd, initialIdea: idea, mainImage };
        }
        return r;
      }));
    }
  };

  const deductPoints = async (amount: number, description: { zh: string; en: string }, projectName?: string) => {
   // Optimistic update
    setPoints(prev => Math.max(0, prev - amount));
    
    // Refresh points and history from server
    try {
      // getUserPoints();
      if (showPointsModal) {
        loadPointHistory(1, false);
      }
    } catch (e) {
      console.error("Failed to sync points after deduction", e);
    }
  };

  const t = {
    zh: {
      newProject: "创建新创意",
      history: "创意归档库",
      emptyHistory: "暂无项目记录",
      backHome: "返回实验室",
      active: "进行中",
      points: "账户点数",
      pointDetail: "点数明细",
      transaction: "交易记录",
      noTransaction: "暂无点数消耗记录",
      projectsHub: "我的创意项目",
      viewAll: "查看全部",
      belongsTo: "所属项目：",
      logout: "退出登录",
      login: "登录 / 注册",
      appTitle: "AI造物实验室"
    },
    en: {
      newProject: "New Project",
      history: "Creative Archive",
      emptyHistory: "Your ideas will appear here",
      backHome: "Back to Lab",
      active: "Active",
      points: "Credits",
      pointDetail: "Credit Detail",
      transaction: "History",
      noTransaction: "No transaction records",
      projectsHub: "My Projects",
      viewAll: "View All",
      belongsTo: "Project:",
      logout: "Logout",
      login: "Login / Sign Up",
      appTitle: "AI Creator Laboratory"
    }
  }[lang];

  return (
    <div className="h-screen w-screen flex flex-col bg-transparent overflow-hidden text-black font-sans selection:bg-red-100 selection:text-red-600">
      
      {authSuccessToast.show && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[2000] animate-fade-down">
          <div className="bg-white/90 backdrop-blur-xl border border-emerald-100 px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-100">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-emerald-600 tracking-wider">Success</span>
              <span className="text-sm font-black text-slate-900">
                {lang === 'zh' 
                  ? (authSuccessToast.type === 'register' ? '注册成功！欢迎加入实验室' : '登录成功！欢迎回来')
                  : (authSuccessToast.type === 'register' ? 'Registration Successful! Welcome to the Lab' : 'Login Successful! Welcome Back')
                }
              </span>
            </div>
          </div>
        </div>
      )}

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onSuccess={(u, isNewUser) => {
          setUser(u);
          setPoints(u.remaining_ai_token);
          setAuthSuccessToast({show: true, type: isNewUser ? 'register' : 'login'});
          setTimeout(() => setAuthSuccessToast({show: false, type: 'login'}), 3000);
        }}
        lang={lang}
      />

      <ConfirmModal
        isOpen={confirmDelete.show}
        title={lang === 'zh' ? '确认删除' : 'Confirm Delete'}
        message={lang === 'zh' ? '确定要删除这个项目吗？此操作不可撤销。' : 'Are you sure you want to delete this project? This action cannot be undone.'}
        onConfirm={executeDelete}
        onCancel={() => setConfirmDelete({ show: false, recordId: null })}
        confirmText={lang === 'zh' ? '确认删除' : 'Delete'}
        cancelText={lang === 'zh' ? '取消' : 'Cancel'}
        type="danger"
      />

      {errorToast?.show && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[2000] animate-fade-up">
          <div className="bg-red-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-red-400">
            <X className="w-4 h-4" />
            <span className="text-sm font-black">{errorToast.message}</span>
          </div>
        </div>
      )}

      {showPointsModal && (
        <div className="fixed inset-0 z-[600] bg-slate-950/60 flex items-center justify-center p-6 animate-fade-up">
           <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] border border-slate-100">
              <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                 <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center shadow-lg shadow-yellow-100">
                       <History className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-black text-black tracking-tight">{t.pointDetail}</h2>
                 </div>
                 <button onClick={() => setShowPointsModal(false)} className="w-10 h-10 flex items-center justify-center hover:bg-slate-200 rounded-full transition-all hover:scale-110 active:scale-90"><X /></button>
              </div>
              
              <div 
                className="flex-1 overflow-y-auto custom-scrollbar p-8 space-y-4"
                onScroll={(e) => {
                  const target = e.currentTarget;
                  if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
                    loadMorePoints();
                  }
                }}
              >
                 <div className="flex flex-col items-center py-6 bg-slate-900 text-white rounded-xl mb-6 shadow-xl relative overflow-hidden">
                    <Zap className="absolute -right-4 -bottom-4 w-24 h-24 text-white/5 rotate-12" />
                    <span className="text-[11px] font-black tracking-[0.3em] opacity-40 mb-2">{t.points}</span>
                    <span className="text-4xl font-black tracking-tighter">{points} <span className="text-xl opacity-50">Credits</span></span>
                 </div>

                 {pointHistory.length === 0 && !isLoadingPoints ? (
                   <div className="text-center py-20 opacity-30">
                      <Coins className="w-12 h-12 mx-auto mb-4" />
                      <p className="text-[12px] font-black tracking-widest">{t.noTransaction}</p>
                   </div>
                 ) : (
                   <div className="space-y-3">
                      {pointHistory.map(tx => (
                        <div key={tx.id} className="p-5 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-between group hover:bg-white transition-all">
                           <div className="flex flex-col gap-1 max-w-[80%]">
                              <span className="text-sm font-black text-black leading-tight">{tx.description[lang]}</span>
                              {tx.projectName && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <Tag className="w-3 h-3 text-red-500" />
                                  <span className="text-[10px] font-bold text-red-600/60 tracking-tight truncate">{t.belongsTo}{tx.projectName}</span>
                                </div>
                              )}
                              <span className="text-[11px] font-bold text-slate-400 tracking-wider mt-1">{new Date(tx.timestamp).toLocaleString()}</span>
                           </div>
                           <div className="text-red-600 font-black text-lg">-{tx.amount}</div>
                        </div>
                      ))}
                      
                      {isLoadingPoints && (
                        <div className="py-4 text-center">
                          <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </div>
                      )}
                      
                      {!isLoadingPoints && hasMorePoints && (
                        <button 
                          onClick={loadMorePoints}
                          className="w-full py-3 text-[10px] font-black tracking-widest text-slate-400 hover:text-red-600 hover:bg-slate-50 rounded-lg transition-all active:scale-95"
                        >
                          {lang === 'zh' ? '加载更多' : 'Load More'}
                        </button>
                      )}
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}

      <header className="h-14 shrink-0 border-b border-slate-100/50 flex items-center justify-between px-8 bg-white/40 backdrop-blur-md z-[100]">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={createNewProject}>
            <div className="w-10 h-10 flex items-center justify-center rounded-lg group-hover:rotate-12 transition-all">
              <img src="/rdlogo.svg" alt="Logo" className="w-8 h-8" referrerPolicy="no-referrer" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-app-title font-bold tracking-tight text-black leading-none">{t.appTitle}</h1>
            </div>
          </div>

          <div className="w-[1px] h-8 bg-slate-100 mx-2"></div>

          <div className="relative group">
            <button className="flex items-center gap-3 px-5 h-9 bg-white border border-slate-100 text-black rounded-lg shadow-sm hover:bg-slate-50 hover:scale-[1.02] active:scale-95 transition-all text-[12px] font-black tracking-widest">
              <FolderOpen className="w-4 h-4 text-slate-400 group-hover:text-red-500 transition-colors" />
              <span>{t.projectsHub}</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-30" />
            </button>

            <div className="absolute top-full left-0 pt-3 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300 ease-out z-[110]">
              <div className="w-80 bg-white/95 backdrop-blur-md rounded-lg shadow-2xl shadow-slate-200/80 border border-slate-100 overflow-hidden flex flex-col max-h-[80vh]">
                <div className="p-4 border-b border-slate-50">
                  <button 
                    onClick={createNewProject}
                    className="w-full py-3.5 bg-slate-100 text-black rounded-lg flex items-center justify-center gap-3 hover:bg-red-600 hover:text-white transition-all text-[12px] font-black tracking-widest group/btn"
                  >
                    <Plus className="w-4 h-4 group-hover/btn:rotate-90 transition-transform" />
                    {t.newProject}
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <div className="px-6 py-4 flex items-center justify-between border-b border-slate-50/50">
                    <span className="text-[11px] font-black text-slate-400 tracking-[0.2em]">{t.history}</span>
                    <History className="w-3.5 h-3.5 text-slate-200" />
                  </div>
                  
                  {historyRecords.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <p className="text-[12px] font-bold text-slate-300 tracking-widest">{t.emptyHistory}</p>
                    </div>
                  ) : (
                    <div className="pb-4">
                      {historyRecords.map((record) => {
                        const previewImages = record.images ? record.images.slice(0, 4) : (record.mainImage ? [record.mainImage] : []);
                        return (
                          <div
                            key={record.id}
                            onClick={() => handleSelectRecord(record)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handleSelectRecord(record);
                              }
                            }}
                            className={`w-full px-6 py-4 flex items-start gap-4 hover:bg-slate-50 transition-all text-left border-l-4 group/item cursor-pointer ${projectId === record.id ? 'border-red-600 bg-red-50/20' : 'border-transparent'}`}
                          >
                            <div className="w-14 h-14 shrink-0 grid grid-cols-2 gap-0.5 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden group-hover/item:scale-105 transition-transform shadow-sm">
                              {previewImages.length > 0 ? (
                                previewImages.map((img, i) => (
                                  <div key={i} className={`bg-slate-100 ${previewImages.length === 1 ? 'col-span-2 row-span-2' : ''} ${previewImages.length === 2 ? 'row-span-2' : ''}`}>
                                    <img src={img} className="w-full h-full object-cover" />
                                  </div>
                                ))
                              ) : (
                                <div className="col-span-2 row-span-2 flex items-center justify-center">
                                  <Zap className="w-4 h-4 text-slate-200" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-black text-black truncate leading-tight">
                                {record.initialIdea || "Concept"}
                              </p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Clock className="w-3 h-3 text-slate-300" />
                                <span className="text-[10px] font-black text-slate-400 tracking-widest">
                                  {new Date(record.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleDeleteProject(e, record.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover/item:opacity-100 transition-all hover:scale-110 active:scale-90"
                              title={lang === 'zh' ? '删除项目' : 'Delete Project'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {user && (
            <button 
              onClick={() => setShowPointsModal(true)}
              className="flex items-center gap-3 px-5 h-9 bg-white/80 border border-slate-100 text-black rounded-lg shadow-lg transition-all hover:scale-105 hover:border-red-200 group relative active:scale-95"
            >
               <div className="relative">
                 <Zap className="w-4 h-4 text-yellow-500 fill-current group-hover:scale-125 transition-transform" />
                 <div className="absolute inset-0 bg-yellow-400/20 blur-md rounded-full group-hover:bg-yellow-400/40 transition-all"></div>
               </div>
               <div className="flex items-center gap-2">
                  <span className="text-[13px] font-black tracking-tighter text-black">{points} Credits</span>
                  <ArrowUpRight className="w-3 h-3 text-slate-300 group-hover:text-red-500 transition-colors" />
               </div>
            </button>
          )}

          <button 
            onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
            className="flex items-center gap-2 px-4 h-9 border border-slate-100 bg-white/30 backdrop-blur-md rounded-lg hover:bg-white/50 transition-all text-slate-500 text-[12px] font-black tracking-widest"
          >
            <Globe className="w-3.5 h-3.5" />
            <span>{lang === 'zh' ? 'EN' : 'CN'}</span>
          </button>

          <div className="w-[1px] h-8 bg-slate-100 mx-1"></div>

          {user ? (
            <div className="flex items-center gap-3 pl-2">
              <div className="flex flex-col items-end">
                <span className="text-[11px] font-black text-black leading-none">{user.firstName} {user.lastName}</span>
                <span className="text-[9px] font-bold text-slate-400 tracking-tighter mt-1">{user.email}</span>
              </div>
              <button 
                onClick={() => handleLogout()}
                className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-100 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all hover:scale-105 active:scale-90"
                title={t.logout}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setShowAuthModal(true)}
              className="flex items-center gap-2 px-6 h-9 bg-slate-900 text-white rounded-lg hover:bg-black hover:scale-[1.02] active:scale-95 transition-all text-[11px] font-black tracking-widest shadow-lg shadow-slate-200"
            >
              <UserIcon className="w-3.5 h-3.5" />
              <span>{t.login}</span>
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 min-h-0 relative overflow-hidden flex flex-col bg-transparent">
        {currentStep === AppStep.IDEATION && (
          <IdeationStep 
            key={projectId}
            projectId={projectId}
            points={points}
            user={user}
            onAuthRequired={handleAuthRequired}
            deductPoints={deductPoints}
            onComplete={handlePrdComplete} 
            onSelectRecord={handleSelectRecord}
            onProjectCreated={(record) => {
              setHistoryRecords(prev => {
                // Avoid duplicates if any
                if (prev.some(r => r.id === record.id)) return prev;
                return [record, ...prev];
              });
            }}
            refreshHistory={loadHistory}
            onNewProject={createNewProject}
            lang={lang} 
            historyRecords={historyRecords}
          />
        )}
        
        {showArchiveToast && (
          <div className="absolute bottom-8 right-8 z-[60] animate-fade-up transition-opacity duration-500">
            <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-4 border-2 border-green-400/50 backdrop-blur-md">
               <CheckCircle2 className="w-6 h-6" />
               <div className="flex flex-col">
                  <span className="text-[10px] font-black opacity-60 tracking-widest">Project Sync</span>
                  <span className="text-app-body font-black">{lang === 'zh' ? '设计方案已实时归档' : 'Design Synced'}</span>
               </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
